import {sample, compact, isEmpty} from 'lodash';
import {
    useCallback,
    useEffect,
    useRef,
    useState,
    forwardRef,
    useImperativeHandle,
} from 'react';

import {
    Sgf,
    calcMatAndMarkup,
    Ki,
    Cursor,
    addMoveToCurrentNode,
    zeros,
    empty,
    inRightPath,
    inWrongPath,
    inTargetPath,
    isRightNode,
    isWrongNode,
    canMove,
    calcPreventMoveMat,
    calcVisibleArea,
    Effect,
    TNode,
    isMoveNode,
    a1ToPos,
    Markup,
    extractBoardSize,
} from 'ghostban';
import {Board, BoardRefType} from './Board';
import {AnimCorrect, AnimIncorrect, AnimSpeed} from './animations';

import {
    BoardSettings,
    Problem,
    ProblemComment,
    ProblemMinimalWithSgf,
    ResponseSpeed,
    RushProblem,
    SingleProblem,
} from '@types';
import {
    getFirstSetupNodeFromSgf,
    getFirstToMoveColor,
    getUndoMove,
    GHOSTBAN_OPTIONS,
    isOffPath,
    isSpeedSolve,
    isSpeedPlay,
    useDispatch,
    useSelector,
    calcCommentAndOffPathMarkup,
    convertSgfPathToCompressedPath,
    SPEED_PLAY_COLOR,
} from '@utils';
import {boardSlice} from '@reducers';

type ProblemBoardProps = {
    problem:
        | Problem
        | RushProblem
        | ProblemMinimalWithSgf
        | SingleProblem
        | null;
    problemComments?: ProblemComment[];
    blur?: boolean;
    grayscale?: boolean;
    disabled?: boolean;
    onCorrect?: (path: string, solveTime: number, moveTimes: number[]) => void;
    onIncorrect?: (
        path: string,
        solveTime: number,
        moveTimes: number[]
    ) => void;
    onAfterMove?: (
        move: TNode,
        path: string,
        solveTime: number,
        moveTimes: number[]
    ) => void;
    onAfterResponse?: (
        move: TNode,
        path: string,
        solveTime: number,
        moveTimes: number[]
    ) => void;
    onCurrentNodeChange?: (node: TNode) => void;
    allowMoveAfterResultCheck?: boolean;
    resetOnIncorrect?: boolean;
    resetOnIncorrectTimeout?: number;
    autoResponse?: boolean;
    responseSpeed?: number;
    animationDuration?: number;
    boardSettings?: BoardSettings;
    boardClassName?: string;
    size?: number | string;
    style?: React.CSSProperties;
    showMarking?: boolean;
    // singleInstance?: boolean;
};

export type ProblemBoardRefType = {
    hideMarking: () => void;
    reset: () => void;
    takeBack: (backToPlayerMove?: boolean) => void;
    resultMarkingVisible: boolean;
};

export const ProblemBoard = forwardRef<ProblemBoardRefType, ProblemBoardProps>(
    (
        {
            problem,
            boardSettings,
            boardClassName = 'board',
            blur = false,
            grayscale = false,
            disabled = false,
            resetOnIncorrect = true,
            resetOnIncorrectTimeout = 500,
            autoResponse = true,
            responseSpeed = ResponseSpeed.NORMAL,
            animationDuration = 200,
            allowMoveAfterResultCheck = false,
            size = '100vmin',
            style = {},
            showMarking = true,
            onCorrect,
            onIncorrect,
            onAfterMove,
            onAfterResponse,
            onCurrentNodeChange,
        },
        ref
    ) => {
        const boardRef = useRef<BoardRefType>(null);
        const {currentNode, activeBranchIndexes, highlights} = useSelector(
            s => s.board
        );
        const [mat, setMat] = useState<number[][]>(zeros([19, 19]));
        const [initVisibleAreaMat, setInitVisibleAreaMat] = useState<
            number[][]
        >(zeros([19, 19]));
        const [visibleAreaMat, setVisibleAreaMat] = useState<number[][]>(
            zeros([19, 19])
        );
        const [markup, setMarkup] = useState<string[][]>(empty([19, 19]));
        const [effectMat] = useState<string[][]>(empty([19, 19]));
        const [preventMoveMat, setPreventMoveMat] = useState<number[][]>(
            zeros([19, 19])
        );
        const [turn, setTurn] = useState<Ki>(Ki.Empty);
        const [cursor, setCursor] = useState<Cursor>(Cursor.None);
        const [correctMarkingVisible, setCorrectMarkingVisible] =
            useState(false);
        const [incorrectMarkingVisible, setIncorrectMarkingVisible] =
            useState(false);
        const [speedMarkingVisible, setSpeedMarkingVisible] = useState(false);
        const [speedPlayMarkingVisible, setSpeedPlayMarkingVisible] =
            useState(false);
        const [responding, setResponding] = useState(false);
        const [resultChecking, setResultChecking] = useState(false);
        const [initialTime, setInitialTime] = useState<number>(Date.now());
        const [hasUsedTakeBack, setHasUsedTakeBack] = useState(false);
        const [hasUsedReset, setHasUsedReset] = useState(false);

        const moveTimesRef = useRef<number[]>([]);
        const [lastMoveTime, setLastMoveTime] = useState<number>(Date.now());
        const dispatch = useDispatch();

        const problemId = problem?.id;
        const problemSgf = problem?.sgf;

        const handleBoardClick = () => {
            const board = boardRef.current?.getBoard();
            if (
                disabled ||
                responding ||
                !currentNode ||
                turn === Ki.Empty ||
                !board ||
                !board.dom ||
                !problem
            ) {
                return;
            }

            const i = board.cursorPosition[0];
            const j = board.cursorPosition[1];

            const ai = board.actualCursorPosition[0];
            const aj = board.actualCursorPosition[1];
            if (mat[ai][aj] === Ki.Empty && preventMoveMat[ai][aj] === 1) {
                effectMat[ai][aj] = Effect.Ban;
                board.setEffectMat(effectMat);
                board.playEffect();
            }

            if (i < 0 || j < 0) return;
            if (i > 18 || j > 18) return;

            const va = calcVisibleArea(
                initVisibleAreaMat,
                GHOSTBAN_OPTIONS.extent
            );
            if (i < va[0][0] || i > va[0][1]) {
                return;
            }

            if (j < va[1][0] || j > va[1][1]) {
                return;
            }

            const previousBoardState = currentNode.parent
                ? calcMatAndMarkup(currentNode.parent).mat
                : null;
            if (canMove(mat, i, j, turn, previousBoardState)) {
                setResponding(true);
                const moveAudio = boardRef.current?.getMoveAudio();
                if (!disabled && boardSettings?.moveSound && moveAudio) {
                    void moveAudio.play();
                }

                const node = addMoveToCurrentNode(currentNode, mat, i, j, turn);
                if (node) {
                    dispatch(boardSlice.actions.setCurrentNode(node));
                    const path = getPath(node);
                    const solveTime = Date.now() - initialTime;
                    const moveTime = Date.now() - lastMoveTime;
                    const updatedMoveTimes = [
                        ...moveTimesRef.current,
                        moveTime,
                    ];
                    moveTimesRef.current = updatedMoveTimes;
                    setLastMoveTime(Date.now());

                    if (autoResponse) {
                        generateMove(node, problem.sgf);
                    } else {
                        setResponding(false);
                    }

                    if (onAfterMove)
                        onAfterMove(node, path, solveTime, updatedMoveTimes);
                }
            }
        };

        const getPath = (node: TNode) => {
            const newPath = compact(
                node.getPath().map(n => n.model.moveProps[0]?.toString())
            ).join(';');
            return newPath;
        };

        const checkResult = (node: TNode, sgf: string) => {
            // We need to check right node even if it has children
            const path = getPath(node);
            if (isRightNode(node)) {
                setResultChecking(true);
                makeCorrect(path);
                return;
            }

            if (
                node.children.filter((n: TNode) => !isOffPath(n, sgf))
                    .length === 0
            ) {
                setResultChecking(true);
                if (isWrongNode(node)) {
                    makeIncorrect(path);
                }
            }
        };

        const generateMove = (node: TNode, sgf: string) => {
            const handleAfterResponse = (responseNode: TNode) => {
                setResponding(false);
                setLastMoveTime(Date.now());
                const path = getPath(responseNode);
                const solveTime = Date.now() - initialTime;
                if (onAfterResponse) {
                    onAfterResponse(
                        responseNode,
                        path,
                        solveTime,
                        moveTimesRef.current
                    );
                }
                checkResult(responseNode, sgf);
            };

            if (node.hasChildren()) {
                const correctTargetNodes = node.children.filter(
                    (n: TNode) =>
                        inRightPath(n) && inTargetPath(n) && !isOffPath(n, sgf)
                );

                // If there are no target path, we need to find the first correct node
                const correctNodes = node.children.filter(
                    (n: TNode) =>
                        inRightPath(n) &&
                        n.getIndex() === 0 &&
                        !isOffPath(n, sgf)
                );

                const incorrectTargetNodes = node.children.filter(
                    (n: TNode) =>
                        inWrongPath(n) && inTargetPath(n) && !isOffPath(n, sgf)
                );

                // Find the top incorrect node
                const incorrectNodes = node.children.filter(
                    (n: TNode) =>
                        inWrongPath(n) &&
                        !inTargetPath(n) &&
                        n.getIndex() === 0 &&
                        !isOffPath(n, sgf)
                );

                let nextNode: TNode | undefined = node;

                if (!isOffPath(node, sgf)) {
                    if (isRightNode(node)) {
                        nextNode = node;
                    } else if (correctTargetNodes.length > 0) {
                        nextNode = sample(correctTargetNodes);
                    } else if (correctNodes.length > 0) {
                        nextNode = sample(correctNodes);
                    } else if (incorrectTargetNodes.length > 0) {
                        nextNode = sample(incorrectTargetNodes);
                    } else if (incorrectNodes.length > 0) {
                        nextNode = sample(incorrectNodes);
                    }
                }

                if (nextNode === node) {
                    handleAfterResponse(nextNode);
                } else {
                    setTimeout(() => {
                        dispatch(boardSlice.actions.setCurrentNode(nextNode));
                        if (nextNode) {
                            handleAfterResponse(nextNode);
                        }
                    }, responseSpeed);
                }
            } else {
                setResponding(false);
                setLastMoveTime(Date.now());
                checkResult(node, sgf);
            }
        };

        const makeCorrect = (path: string) => {
            const solveTime = Date.now() - initialTime;
            const latestMoveTimes = moveTimesRef.current;

            const shouldSpeedSolve =
                !hasUsedTakeBack &&
                !hasUsedReset &&
                isSpeedSolve(latestMoveTimes);
            const shouldSpeedPlay =
                !hasUsedTakeBack &&
                !hasUsedReset &&
                isSpeedPlay(latestMoveTimes);

            if (shouldSpeedSolve) {
                setSpeedMarkingVisible(true);
                setSpeedPlayMarkingVisible(false);
                setCorrectMarkingVisible(false);
            } else if (shouldSpeedPlay) {
                setSpeedPlayMarkingVisible(true);
                setSpeedMarkingVisible(false);
                setCorrectMarkingVisible(false);
            } else {
                setCorrectMarkingVisible(true);
                setSpeedMarkingVisible(false);
                setSpeedPlayMarkingVisible(false);
            }
            setIncorrectMarkingVisible(false);

            if (onCorrect) onCorrect(path, solveTime, latestMoveTimes);
            if (allowMoveAfterResultCheck) {
                setResultChecking(false);
                setResponding(false);
            }
        };

        const makeIncorrect = (path: string) => {
            setCorrectMarkingVisible(false);
            setSpeedMarkingVisible(false);
            setSpeedPlayMarkingVisible(false);
            setIncorrectMarkingVisible(true);
            const solveTime = Date.now() - initialTime;
            const latestMoveTimes = moveTimesRef.current;
            if (onIncorrect) onIncorrect(path, solveTime, latestMoveTimes);
            if (allowMoveAfterResultCheck) {
                setResultChecking(false);
                setResponding(false);
            }
            if (resetOnIncorrect) {
                setTimeout(() => {
                    handleReset();
                }, resetOnIncorrectTimeout);
            }
        };

        const renderBoard = useCallback(
            (node: TNode) => {
                const board = boardRef.current?.getBoard();
                if (board && board.dom) {
                    const {mat, markup, numMarkup} = calcMatAndMarkup(node);
                    const preventMoveMat = calcPreventMoveMat(node);
                    let newMarkup = markup;
                    const activeIndex = activeBranchIndexes[node.model.id];
                    if (boardSettings?.variationsIndicator) {
                        // newMarkup = calcVariationsMarkup(node);
                        newMarkup = calcCommentAndOffPathMarkup(
                            node,
                            activeIndex,
                            problem?.sgf
                        );
                    }

                    if (highlights && highlights.length > 0) {
                        const boardSize = extractBoardSize(node);
                        for (const highlight of highlights) {
                            const {x, y} = a1ToPos(highlight);
                            if (
                                x >= 0 &&
                                x < boardSize &&
                                y >= 0 &&
                                y < boardSize
                            ) {
                                if (isEmpty(newMarkup[x][y])) {
                                    newMarkup[x][y] = Markup.Highlight;
                                } else {
                                    newMarkup[x][y] += `|${Markup.Highlight}`;
                                }
                            }
                        }
                    }

                    if (boardSettings?.number) {
                        for (let i = 0; i < numMarkup.length; i++) {
                            for (let j = 0; j < numMarkup[i].length; j++) {
                                if (numMarkup[i][j] !== '') {
                                    newMarkup[i][j] = numMarkup[i][j];
                                }
                            }
                        }
                    }
                    setMat(mat);
                    setMarkup(newMarkup);
                    setPreventMoveMat(preventMoveMat);
                }
            },
            [
                boardSettings?.number,
                boardSettings?.variationsIndicator,
                highlights,
                activeBranchIndexes,
                problem?.sgf,
            ]
        );

        const calcNextColor = (node: TNode) => {
            if (isMoveNode(node)) {
                if (node.model.moveProps[0]?.token === 'W') {
                    setCursor(Cursor.BlackStone);
                    setTurn(Ki.Black);
                } else if (node.model.moveProps[0]?.token === 'B') {
                    setCursor(Cursor.WhiteStone);
                    setTurn(Ki.White);
                } else {
                    setCursor(Cursor.None);
                    setTurn(Ki.Empty);
                }
            } else {
                const sgf = new Sgf(node);
                const sgfTxt = sgf.toSgf();
                const ki = getFirstToMoveColor(sgfTxt);
                setTurn(ki);
                if (ki === Ki.White) setCursor(Cursor.WhiteStone);
                if (ki === Ki.Black) setCursor(Cursor.BlackStone);
                if (ki === Ki.Empty) setCursor(Cursor.None);
            }
        };

        const handleReset = useCallback(() => {
            if (!problemId || !problemSgf) return;
            setResultChecking(false);
            setResponding(false);
            setCorrectMarkingVisible(false);
            setIncorrectMarkingVisible(false);
            setSpeedMarkingVisible(false);
            setSpeedPlayMarkingVisible(false);
            setLastMoveTime(initialTime);
            moveTimesRef.current = [];
            const board = boardRef.current?.getBoard();
            if (board && board.dom) {
                board.cursorPosition = [-1, -1];
                // const sgfString = hardReset ? problem.sgf : sgf;
                const node = getFirstSetupNodeFromSgf(problemSgf);
                if (!node) return;

                dispatch(boardSlice.actions.setCurrentNode(node));
                const {visibleAreaMat} = calcMatAndMarkup(node);
                setInitVisibleAreaMat(visibleAreaMat);
                setVisibleAreaMat(visibleAreaMat);
                renderBoard(node);
            }
        }, [problemId, problemSgf, initialTime, renderBoard, dispatch]);

        const handleTakeBack = useCallback(
            (backToPlayerMove = true) => {
                if (!problemId || !problemSgf || !currentNode) return;
                setResultChecking(false);
                setResponding(false);
                setCorrectMarkingVisible(false);
                setIncorrectMarkingVisible(false);
                setSpeedMarkingVisible(false);
                setSpeedPlayMarkingVisible(false);
                const lastMoveTime =
                    moveTimesRef.current[moveTimesRef.current.length - 1];
                moveTimesRef.current = moveTimesRef.current.slice(0, -1);
                setLastMoveTime(Date.now() - (lastMoveTime || 0));
                const board = boardRef.current?.getBoard();
                if (board && board.dom) {
                    board.cursorPosition = [-1, -1];
                    let node = currentNode?.parent;
                    if (backToPlayerMove) {
                        node = getUndoMove(currentNode, problemSgf);
                    }
                    if (!node) return;

                    dispatch(boardSlice.actions.setCurrentNode(node));
                    const {visibleAreaMat} = calcMatAndMarkup(node);
                    setInitVisibleAreaMat(visibleAreaMat);
                    setVisibleAreaMat(visibleAreaMat);
                    renderBoard(node);
                }
            },
            [problemId, problemSgf, renderBoard, dispatch, currentNode]
        );

        useEffect(() => {
            if (!problemId) return;
            moveTimesRef.current = [];
            handleReset();
            setHasUsedReset(false);
            setHasUsedTakeBack(false);
            setInitialTime(Date.now());
            setLastMoveTime(Date.now());
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [problemId]);

        useEffect(() => {
            if (!currentNode) return;
            const path = getPath(currentNode);
            dispatch(boardSlice.actions.setPath(path));
            dispatch(
                boardSlice.actions.setCompressedPath(
                    convertSgfPathToCompressedPath(path)
                )
            );
            renderBoard(currentNode);
            calcNextColor(currentNode);
            if (onCurrentNodeChange) onCurrentNodeChange(currentNode);
        }, [
            currentNode,
            renderBoard,
            dispatch,
            onCurrentNodeChange,
            activeBranchIndexes,
        ]);

        useImperativeHandle(ref, () => ({
            hideMarking: (resetCursor = true) => {
                setCorrectMarkingVisible(false);
                setIncorrectMarkingVisible(false);
                setSpeedMarkingVisible(false);
                setSpeedPlayMarkingVisible(false);
                setResultChecking(false);
                setResponding(false);
                // Hide ghost cursor
                if (resetCursor) {
                    const board = boardRef.current?.getBoard();
                    if (board && board.dom) {
                        board.cursorPosition = [-1, -1];
                    }
                }
            },
            reset: () => {
                setHasUsedReset(true);
                handleReset();
            },
            takeBack: (backToPlayerMove = true) => {
                setHasUsedTakeBack(true);
                handleTakeBack(backToPlayerMove);
            },
            // Prevent send solve request multiple times after problem is solved/failed
            resultMarkingVisible:
                correctMarkingVisible ||
                incorrectMarkingVisible ||
                speedMarkingVisible,
        }));

        return (
            <div
                style={style}
                className={`flex flex-col lg:flex-row relative items-start justify-start w-fit h-fit ${grayscale ? 'fliter grayscale' : ''} `}>
                <Board
                    ref={boardRef}
                    boardOptions={{
                        interactive:
                            !disabled && !responding && !resultChecking,
                        zoom: true,
                        ...boardSettings,
                    }}
                    boardClassName={boardClassName}
                    size={size}
                    mat={mat}
                    markup={markup}
                    visibleAreaMat={visibleAreaMat}
                    preventMoveMat={preventMoveMat}
                    effectMat={effectMat}
                    turn={turn}
                    cursor={cursor}
                    disabled={disabled || responding || resultChecking}
                    onClick={handleBoardClick}
                />
                {blur && (
                    <div
                        className={`${boardClassName} absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none backdrop-blur-sm`}
                    />
                )}
                <div
                    style={{
                        width: '50vmin',
                        height: '50vmin',
                        display:
                            showMarking && correctMarkingVisible
                                ? 'block'
                                : 'none',
                    }}
                    className={
                        'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none opacity-65'
                    }>
                    <AnimCorrect
                        visible={showMarking && correctMarkingVisible}
                        duration={animationDuration}
                    />
                </div>
                <div
                    style={{
                        width: '50vmin',
                        height: '50vmin',
                        display:
                            showMarking && incorrectMarkingVisible
                                ? 'block'
                                : 'none',
                    }}
                    className={
                        'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none opacity-65'
                    }>
                    <AnimIncorrect
                        visible={showMarking && incorrectMarkingVisible}
                        duration={animationDuration}
                    />
                </div>
                <div
                    style={{
                        width: '50vmin',
                        height: '50vmin',
                        display:
                            showMarking && speedMarkingVisible
                                ? 'block'
                                : 'none',
                    }}
                    className={
                        'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none opacity-90'
                    }>
                    <AnimSpeed
                        visible={showMarking && speedMarkingVisible}
                        duration={animationDuration}
                    />
                </div>
                <div
                    style={{
                        width: '50vmin',
                        height: '50vmin',
                        display:
                            showMarking && speedPlayMarkingVisible
                                ? 'block'
                                : 'none',
                    }}
                    className={
                        'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none opacity-90'
                    }>
                    <AnimSpeed
                        visible={showMarking && speedPlayMarkingVisible}
                        duration={animationDuration}
                        color={SPEED_PLAY_COLOR}
                    />
                </div>
            </div>
        );
    }
);

ProblemBoard.displayName = 'ProblemBoard';
